import { useState, useEffect, useCallback } from 'react';
import {
    fetchStudents as apiFetchStudents,
    fetchClasses,
    fetchSections,
    mapStudentToUi,
} from '../../services/studentApi';
import type { ClassOption, SectionOption, StudentApiRecord } from '../../services/studentApi';
import { fetchFeeDiscounts, assignDiscountToStudent, DISCOUNT_CALC_TYPE_OPTIONS } from '../../services/feeDiscountApi';
import type { FeeDiscountRecord } from '../../services/feeDiscountApi';
import { getSchoolId } from '../../services/auth';
import type { ApiError } from '../../services/api';
import './StudentDiscounts.css';

type StudentRow = {
    id: string;
    student_name: string;
    class_name: string | null;
    section_name: string | null;
};

type PaginationMeta = {
    current_page: number;
    /** Not sent by the students endpoint; derive the row range from current_page/per_page. */
    from?: number;
    last_page: number;
    per_page: number;
    total: number;
};

const PAGE_SIZE = 15;

export default function StudentDiscounts() {
    const [schoolId] = useState(() => getSchoolId());

    /* ── Filter state (draft / applied) ── */
    const [nameInput, setNameInput] = useState('');
    const [classInput, setClassInput] = useState('');
    const [sectionInput, setSectionInput] = useState('');
    const [appliedName, setAppliedName] = useState('');
    const [appliedClassId, setAppliedClassId] = useState('');
    const [appliedSectionId, setAppliedSectionId] = useState('');

    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);

    /* ── Student data ── */
    const [students, setStudents] = useState<StudentRow[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>({ current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    /* ── Discounts ── */
    const [discounts, setDiscounts] = useState<FeeDiscountRecord[]>([]);
    const [selectedDiscount, setSelectedDiscount] = useState<Record<string, string>>({});
    const [assigningId, setAssigningId] = useState<string | null>(null);

    /* ── Feedback ── */
    const [successMsg, setSuccessMsg] = useState('');
    const [apiError, setApiError] = useState('');

    /* ── Load classes on mount ── */
    useEffect(() => {
        fetchClasses()
            .then((cls) => setAvailableClasses(cls))
            .catch(() => {});
    }, []);

    /* ── Load discounts on mount ── */
    useEffect(() => {
        if (!schoolId) return;
        fetchFeeDiscounts({ school_id: schoolId })
            .then((data) => setDiscounts(data.filter((d) => d.is_active)))
            .catch(() => {});
    }, [schoolId]);

    /* ── Cascade: sections when class changes ── */
    useEffect(() => {
        if (classInput) {
            fetchSections(classInput)
                .then((s) => setAvailableSections(s))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
        setSectionInput('');
    }, [classInput]);

    /* ── Fetch students ── */
    const loadStudents = useCallback(async () => {
        setLoading(true);
        setApiError('');
        try {
            const res = await apiFetchStudents({
                page,
                perPage: PAGE_SIZE,
                name: appliedName || undefined,
                classId: appliedClassId || undefined,
                sectionId: appliedSectionId || undefined,
            });
            setStudents(res.data.map((s: StudentApiRecord) => {
                const ui = mapStudentToUi(s);
                return { id: ui.id, student_name: ui.student_name, class_name: ui.class_name, section_name: ui.section_name };
            }));
            setMeta(res.meta);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to load students.');
        } finally {
            setLoading(false);
        }
    }, [page, appliedName, appliedClassId, appliedSectionId]);

    useEffect(() => { loadStudents(); }, [loadStudents]);

    /* Reset page when filters change */
    useEffect(() => { setPage(1); }, [appliedName, appliedClassId, appliedSectionId]);

    /* ── Handlers ── */
    const handleSearch = () => {
        setAppliedName(nameInput);
        setAppliedClassId(classInput);
        setAppliedSectionId(sectionInput);
    };

    const handleClear = () => {
        setNameInput(''); setClassInput(''); setSectionInput('');
        setAppliedName(''); setAppliedClassId(''); setAppliedSectionId('');
    };

    const hasActiveFilters = appliedName || appliedClassId || appliedSectionId;

    /* ── Displayed row range (the API meta has no `from`, so derive it) ── */
    const rowsPerPage = meta.per_page || PAGE_SIZE;
    const rangeFrom = meta.total === 0 ? 0 : (meta.current_page - 1) * rowsPerPage + 1;
    const rangeTo = Math.min(rangeFrom + rowsPerPage - 1, meta.total);

    const handleAssign = async (studentId: string) => {
        const discountId = selectedDiscount[studentId];
        if (!discountId) return;
        setAssigningId(studentId);
        setApiError('');
        try {
            await assignDiscountToStudent(studentId, discountId);
            const discountName = discounts.find((d) => d.id === discountId)?.name ?? 'Discount';
            setSuccessMsg(`${discountName} assigned successfully!`);
            setSelectedDiscount((prev) => {
                const next = { ...prev };
                delete next[studentId];
                return next;
            });
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to assign discount.');
        } finally {
            setAssigningId(null);
        }
    };

    const formatDiscountValue = (d: FeeDiscountRecord) =>
        d.calculation_type === 'percentage' ? `${d.value}%` : `₹${Number(d.value).toLocaleString('en-IN')}`;

    /* ── Pagination helpers ── */
    const pageNumbers = (): number[] => {
        const pages: number[] = [];
        const start = Math.max(1, meta.current_page - 2);
        const end = Math.min(meta.last_page, meta.current_page + 2);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="sd">
            {/* ── Error Banner ── */}
            {apiError && (
                <div className="sd__error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                    <span>{apiError}</span>
                    <button className="sd__error-dismiss" onClick={() => setApiError('')}>×</button>
                </div>
            )}

            {/* ── Success Toast ── */}
            {successMsg && (
                <div className="sd__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="sd__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="sd__toolbar">
                <div className="sd__toolbar-left">
                    <div className="sd__search-wrapper">
                        <svg className="sd__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            className="sd__search"
                            type="text"
                            placeholder="Search by name…"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        {nameInput && (
                            <button className="sd__search-clear" onClick={() => setNameInput('')} aria-label="Clear search">×</button>
                        )}
                    </div>

                    <select className="sd__filter" value={classInput} onChange={(e) => setClassInput(e.target.value)}>
                        <option value="">All Classes</option>
                        {availableClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>

                    <select className="sd__filter" value={sectionInput} onChange={(e) => setSectionInput(e.target.value)} disabled={!classInput}>
                        <option value="">All Sections</option>
                        {availableSections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>

                    <button className="sd__search-btn" onClick={handleSearch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        Search
                    </button>

                    {hasActiveFilters && (
                        <button className="sd__clear-btn" onClick={handleClear} title="Clear all filters">Clear</button>
                    )}
                </div>

                <div className="sd__toolbar-right">
                    <span className="sd__record-count">{meta.total} student{meta.total !== 1 ? 's' : ''}</span>
                </div>
            </div>

            {/* ── Data Grid ── */}
            <div className="sd__table-wrap">
                <table className="sd__table">
                    <thead>
                        <tr>
                            <th className="sd__th">Student Name</th>
                            <th className="sd__th">Class</th>
                            <th className="sd__th">Section</th>
                            <th className="sd__th">Discount</th>
                            <th className="sd__th sd__th--actions">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={5} className="sd__loading-cell">
                                    <div className="sd__loader">
                                        <span className="sd__loader-dot" />
                                        <span className="sd__loader-dot" />
                                        <span className="sd__loader-dot" />
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && students.length === 0 && (
                            <tr>
                                <td colSpan={5} className="sd__empty-cell">
                                    <div className="sd__empty">
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35">
                                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                        </svg>
                                        <p>{hasActiveFilters ? 'No students match your filters.' : 'No students found.'}</p>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && students.map((s) => (
                            <tr className="sd__row" key={s.id}>
                                <td className="sd__td sd__td--name">{s.student_name}</td>
                                <td className="sd__td">{s.class_name ?? <span className="sd__muted">—</span>}</td>
                                <td className="sd__td">{s.section_name ?? <span className="sd__muted">—</span>}</td>
                                <td className="sd__td">
                                    <select
                                        className="sd__discount-select"
                                        value={selectedDiscount[s.id] ?? ''}
                                        onChange={(e) => setSelectedDiscount((prev) => ({ ...prev, [s.id]: e.target.value }))}
                                    >
                                        <option value="">Select discount…</option>
                                        {discounts.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.name} ({DISCOUNT_CALC_TYPE_OPTIONS.find((o) => o.value === d.calculation_type)?.label} — {formatDiscountValue(d)})
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="sd__td sd__td--actions">
                                    <button
                                        className="sd__assign-btn"
                                        disabled={!selectedDiscount[s.id] || assigningId === s.id}
                                        onClick={() => handleAssign(s.id)}
                                    >
                                        {assigningId === s.id ? 'Assigning…' : 'Assign'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {!loading && meta.total > 0 && (
                <div className="sd__pagination">
                    <div className="sd__pagination-left">
                        <span className="sd__page-info">
                            Showing {rangeFrom}–{rangeTo} of {meta.total}
                        </span>
                    </div>
                    <div className="sd__pagination-right">
                        <button className="sd__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage(1)} title="First page">«</button>
                        <button className="sd__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage((p) => p - 1)} title="Previous page">‹</button>
                        {pageNumbers().map((n) => (
                            <button
                                key={n}
                                className={`sd__page-btn ${n === meta.current_page ? 'sd__page-btn--active' : ''}`}
                                onClick={() => setPage(n)}
                            >{n}</button>
                        ))}
                        <button className="sd__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)} title="Next page">›</button>
                        <button className="sd__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage(meta.last_page)} title="Last page">»</button>
                    </div>
                </div>
            )}
        </div>
    );
}

